import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation.ts';
import { QuoteIcon, ApexCleanEnergyLogo, OrstedLogo, NextEraEnergyLogo, BrookfieldRenewableLogo, RWELogo, InvenergyLogo } from './Icons.tsx';

const testimonialsData = [
  {
    quote: "Managing over 2,000 PPE items across 8 sites was a nightmare. Shield gave us a central dashboard that ensures 95%+ inspection compliance and total peace of mind.",
    author: "Sarah Jenkins",
    title: "Safety Manager",
    avatar: "https://picsum.photos/100/100?random=2",
    logo: <ApexCleanEnergyLogo className="h-6 text-gray-500 dark:text-gray-400" />,
  },
  {
    quote: "My day starts and ends with the Shield dashboard. I can instantly see what equipment needs inspection, what's out for service, and where everything is. It's cut my admin time by more than half.",
    author: "Michael Chen",
    title: "Equipment Coordinator",
    avatar: "https://picsum.photos/100/100?random=3",
    logo: <OrstedLogo className="h-6 text-gray-500 dark:text-gray-400" />,
  },
  {
    quote: "The QR code scanning is a game-changer. I can pull up an asset's entire history and log an inspection right from my phone in seconds. No more clipboards or paperwork.",
    author: "Elena Rodriguez",
    title: "Field Technician",
    avatar: "https://picsum.photos/100/100?random=4",
    logo: <NextEraEnergyLogo className="h-5 text-gray-500 dark:text-gray-400" />,
  },
  {
    quote: "Allocating the right equipment to each project is critical. With Shield, I can see exactly what's available and assign it with a few clicks. We haven't lost a single piece of equipment since we started.",
    author: "David Lee",
    title: "Site Manager",
    avatar: "https://picsum.photos/100/100?random=5",
    logo: <BrookfieldRenewableLogo className="h-6 text-gray-500 dark:text-gray-400" />,
  },
  {
    quote: "Generating an audit trail used to take us weeks. Now, I can pull a complete, time-stamped logbook for any piece of equipment in seconds. Shield makes us 100% audit-ready, 100% of the time.",
    author: "Maria Garcia",
    title: "Compliance Officer",
    avatar: "https://picsum.photos/100/100?random=6",
    logo: <RWELogo className="h-6 text-gray-500 dark:text-gray-400" />,
  },
  {
    quote: "The efficiency gains are undeniable. Faster assignments, zero lost tools, and streamlined compliance have directly impacted our bottom line. Shield is an essential operational tool.",
    author: "Tom Williamson",
    title: "Operations Director",
    avatar: "https://picsum.photos/100/100?random=7",
    logo: <InvenergyLogo className="h-5 text-gray-500 dark:text-gray-400" />,
  },
];

interface TestimonialCardProps {
  quote: string;
  author: string;
  title: string;
  avatar: string;
  logo: React.ReactNode;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ quote, author, title, avatar, logo }) => (
  <div className="flex-shrink-0 w-full sm:w-[450px] bg-gray-100/70 dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-8 mx-4 flex flex-col justify-between shadow-2xl shadow-orange-600/20 dark:shadow-2xl dark:shadow-[#FF4D00]/20">
    <div className="flex-grow">
      <QuoteIcon className="h-8 w-8 text-orange-500/70 dark:text-[#FF4D00]/50 mb-4" />
      <p className="text-gray-800 dark:text-white leading-relaxed mb-6">"{quote}"</p>
    </div>
    <div className="mt-auto">
        <div className="flex items-center mb-4">
          <img src={avatar} alt={author} className="h-12 w-12 rounded-full mr-4 border-2 border-orange-500/20" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">{author}</p>
            <p className="text-gray-700 dark:text-gray-400 text-sm">{title}</p>
          </div>
        </div>
        <div className="border-t border-black/10 dark:border-white/10 pt-4">
            {logo}
        </div>
    </div>
  </div>
);

const Testimonials: React.FC = () => {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  const allTestimonials = [...testimonialsData, ...testimonialsData];

  return (
    <section id="testimonials" ref={ref} className="py-24 overflow-hidden">
      <div className={`container mx-auto px-6 scroll-animation ${isVisible ? 'visible' : ''}`}>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">Trusted by Teams Who Depend on Their Equipment</h2>
          <p className="text-gray-700 dark:text-gray-400">
            Don't just take our word for it. Here's what our partners have to say about Shield.
          </p>
        </div>
      </div>
      <div className="relative w-full [mask-image:linear-gradient(to_right,transparent,white_10%,white_90%,transparent)]">
        {/* Row 1 - Scrolls Left */}
        <div className="marquee-container flex w-max">
            <div className="marquee-content flex animate-marquee-left">
                {allTestimonials.slice(0, 6).map((testimonial, index) => (
                    <TestimonialCard key={`r1-${index}`} {...testimonial} />
                ))}
            </div>
        </div>
        {/* Row 2 - Scrolls Right */}
        <div className="marquee-container flex w-max mt-8">
            <div className="marquee-content flex animate-marquee-right">
                {allTestimonials.slice(3, 9).map((testimonial, index) => (
                    <TestimonialCard key={`r2-${index}`} {...testimonial} />
                ))}
            </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;